import { find } from 'lodash';

import Subscribable from 'common/provider/subscribable.provider';

export default class CategoryService extends Subscribable() {
    /* @ngInject */
    constructor($q, apiService, storageService) {
        super();

        this.$q = $q;
        this.apiService = apiService;
        this.storageService = storageService;

        this.promises = {};
        this.refreshPromises = {};
    }

    reload(teamId) {
        let object = {};

        object.promise = this.fetchCategories(teamId).then((categories) => {
            object.categories = categories;
            return object.categories;
        }, (result) => {
            object.categories = [];
            return this.$q.reject(result);
        });

        this.promises[teamId] = object;

        return object.promise;
    }
    
    onUpdate(callback) {
        return this.on('change', callback);
    }

    getAll(teamId, reload) {
        if (reload) {
            return this.reload(teamId);
        }

        if (this.promises[teamId]) {
            return this.promises[teamId].promise;
        }

        const cached = this.getCachedCategories(teamId);
        if (cached) {
            this.promises[teamId] = {
                categories: cached,
                promise: this.$q.resolve(cached)
            };

            this.refresh(teamId).catch(() => null);
            return this.promises[teamId].promise;
        }

        return this.reload(teamId);
    }

    getPalette() {
        return this.apiService.get(`/palette`).then((result) => result.data || []);
    }

    find(teamId, callback) {
        return this.getAll(teamId).then((categories) => {
            return find(categories || [], callback) || this.$q.reject(`Category not found.`);
        });
    }

    create(teamId, category) {
        return this.apiService.post(`/teams/${teamId}/categories`, category).then(this.successHandler.bind(this, teamId)).then((createdCategory) => {
            const nextCategory = Object.assign({}, createdCategory || {}, category || {});

            if (createdCategory && createdCategory.id) {
                nextCategory.id = createdCategory.id;
            }

            this.fire('change', nextCategory);
            return createdCategory;
        });
    }

    update(teamId, category) {
        return this.apiService.put(`/categories/${category.id}`, category).then(this.successHandler.bind(this, teamId)).then((updatedCategory) => {
            const nextCategory = Object.assign({}, updatedCategory || {}, category || {});

            if (updatedCategory && updatedCategory.id) {
                nextCategory.id = updatedCategory.id;
            }

            this.fire('change', nextCategory);
            return updatedCategory;
        });
    }

    remove(teamId, category, fallbackCategoryId = null) {
        const config = fallbackCategoryId
            ? {
                params: {
                    fallback: fallbackCategoryId
                }
            }
            : null;

        return this.apiService.delete(`/categories/${category.id}`, config).then(this.successHandler.bind(this, teamId)).then((category) => {
            this.fire('change', category);
            return category;
        });
    }

    reorder(teamId, ids) {
        return this.apiService.post(`/teams/${teamId}/categories/order`, ids).then((result) => {
            return this.reload(teamId).then(() => result.data);
        });
    }

    successHandler(teamId, result) {
        result = result || {};
        return this.reload(teamId).then(() => result.data);
    }

    refresh(teamId) {
        if (this.refreshPromises[teamId]) {
            return this.refreshPromises[teamId];
        }

        const refreshPromise = this.fetchCategories(teamId).then((categories) => {
            this.promises[teamId] = {
                categories,
                promise: this.$q.resolve(categories)
            };

            return categories;
        }).finally(() => {
            delete this.refreshPromises[teamId];
        });

        this.refreshPromises[teamId] = refreshPromise;
        return refreshPromise;
    }

    fetchCategories(teamId) {
        return this.apiService.get(`/teams/${teamId}/categories`).then((result) => {
            result = result || {};
            const categories = result.data || [];
            this.setCachedCategories(teamId, categories);
            return categories;
        });
    }

    getCachedCategories(teamId) {
        const key = this.getCategoriesCacheKey(teamId);
        const cached = this.storageService.getUserItem(key);

        if (!cached) {
            return null;
        }

        if (Array.isArray(cached)) {
            return cached;
        }

        return Array.isArray(cached.categories) ? cached.categories : null;
    }

    setCachedCategories(teamId, categories) {
        try {
            const key = this.getCategoriesCacheKey(teamId);
            this.storageService.setUserItem(key, {
                version: 1,
                updatedAt: Date.now(),
                categories: categories || []
            });
        } catch (error) {
            return null;
        }

        return null;
    }

    getCategoriesCacheKey(teamId) {
        return `categories-${teamId}`;
    }
}



// WEBPACK FOOTER //
// ./src/application/service/calendar/category.service.js